/**
 * Pipedrive Notes Page
 * List of notes with search and pagination
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { usePipedriveNotes } from '@/hooks/use-pipedrive-api'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { StickyNote, Search, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react'
import type { PipedriveNote } from '@/types/pipedrive'

export default function PipedriveNotes() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const limit = 25

  const { data, isLoading, error } = usePipedriveNotes({
    page,
    limit,
    search,
  })

  const notes = data?.data ?? []
  const total = data?.total ?? 0
  const totalPages = data?.pages ?? 1

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Notes</h1>
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          Failed to load notes. Please try again later.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <StickyNote className="h-6 w-6" aria-hidden="true" />
            Notes
          </h1>
          <p className="text-muted-foreground">{total.toLocaleString()} notes</p>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search notes..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-9"
                  aria-label="Search notes"
                />
              </div>
            </div>
            {search && (
              <Button variant="outline" onClick={() => handleSearch('')}>
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40%]">Content</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Deal</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-28" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                  </TableRow>
                ))
              ) : notes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No notes found
                  </TableCell>
                </TableRow>
              ) : (
                notes.map((note: PipedriveNote) => (
                  <TableRow key={note.id}>
                    <TableCell className="max-w-md">
                      <Link
                        to={`/pipedrive/notes/${note.id}`}
                        className="text-primary hover:underline truncate block"
                        title={note.content || undefined}
                      >
                        {note.content || '-'}
                      </Link>
                    </TableCell>
                    <TableCell>{note.user_name || '-'}</TableCell>
                    <TableCell>
                      {note.organization_id ? (
                        <Link
                          to={`/pipedrive/organizations/${note.organization_id}`}
                          className="text-primary hover:underline"
                        >
                          {note.organization || '-'}
                        </Link>
                      ) : (
                        note.organization || '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {note.deal_id ? (
                        <Link
                          to={`/pipedrive/deals/${note.deal_id}`}
                          className="text-primary hover:underline flex items-center gap-1"
                        >
                          {note.deal_title || '-'}
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      ) : (
                        note.deal_title || '-'
                      )}
                    </TableCell>
                    <TableCell>{note.add_time || '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of{' '}
            {total.toLocaleString()}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
